import React, { useState } from 'react';
import { submitAdvertiserInquiry } from '../../services/submitAdvertiserInquiry';

const GOAL_OPTIONS = [
    { value: '', label: 'Please Select' },
    { value: 'brand_awareness', label: 'Brand awareness' },
    { value: 'local_foot_traffic', label: 'Local foot traffic' },
    { value: 'product_launch', label: 'Product launch' },
    { value: 'seasonal_promotion', label: 'Seasonal promotion' },
    { value: 'other', label: 'Other' },
];

const INITIAL_FORM = {
    companyName: '',
    fname: '',
    lname: '',
    email: '',
    phone: '',
    campaignGoals: '',
    message: '',
};

const labelClass = 'block text-sm font-semibold text-invert-navy mb-2';
const fieldClass =
    'w-full px-3.5 py-3 rounded-xl border border-slate-200 bg-white text-invert-navy text-[15px] '
    + 'placeholder:text-slate-400 transition-colors '
    + 'focus:outline-none focus:border-invert-orange focus:ring-[3px] focus:ring-invert-orange/15';
const selectClass = `${fieldClass} appearance-none bg-[length:6px_6px,6px_6px] bg-[position:calc(100%-18px)_calc(50%+2px),calc(100%-12px)_calc(50%+2px)] bg-no-repeat pr-10`;
const selectBg = {
    backgroundImage: 'linear-gradient(45deg, transparent 50%, #64748b 50%), linear-gradient(135deg, #64748b 50%, transparent 50%)',
};
const errorClass = 'text-sm text-red-600 mt-1';
const submitClass =
    'w-full mt-2 px-6 py-4 rounded-xl text-base font-semibold text-white bg-invert-orange '
    + 'hover:bg-invert-orange-deep shadow-lg shadow-invert-orange/20 transition-colors '
    + 'disabled:opacity-60 disabled:cursor-not-allowed';
const ghostClass =
    'inline-flex items-center justify-center px-6 py-3 rounded-xl text-sm font-semibold '
    + 'text-invert-navy border border-slate-200 bg-white hover:bg-slate-50 transition-colors';

function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function Field({ id, label, required, error, children }) {
    return (
        <div className="flex flex-col">
            <label htmlFor={id} className={labelClass}>
                {label}
                {required ? '*' : ''}
            </label>
            {children}
            {error && <span className={errorClass}>{error}</span>}
        </div>
    );
}

export default function AdvertiserInquiryForm({ source = 'landing', className = '' }) {
    const [form, setForm] = useState(INITIAL_FORM);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const updateField = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: '' }));
        }
        if (submitError) {
            setSubmitError('');
        }
    };

    const validate = () => {
        const nextErrors = {};
        if (!form.companyName.trim()) nextErrors.companyName = 'Company name is required';
        if (!form.fname.trim()) nextErrors.fname = 'First name is required';
        if (!form.lname.trim()) nextErrors.lname = 'Last name is required';
        if (!form.email.trim()) {
            nextErrors.email = 'Email is required';
        } else if (!isValidEmail(form.email.trim())) {
            nextErrors.email = 'Enter a valid email address';
        }
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!validate()) return;

        setSubmitting(true);
        setSubmitError('');

        try {
            await submitAdvertiserInquiry({
                companyName: form.companyName.trim(),
                fname: form.fname.trim(),
                lname: form.lname.trim(),
                email: form.email.trim(),
                phone: form.phone.trim() || undefined,
                campaignGoals: form.campaignGoals || undefined,
                message: form.message.trim() || undefined,
                source,
            });
            setSubmitted(true);
            setForm(INITIAL_FORM);
        } catch (err) {
            const message = err.response?.data?.error || 'Something went wrong. Please try again.';
            setSubmitError(message);
        } finally {
            setSubmitting(false);
        }
    };

    const cardClass =
        'rounded-2xl border border-invert-border bg-white p-6 sm:p-8 shadow-sm';

    if (submitted) {
        return (
            <div className={`${cardClass} text-center ${className}`} role="status">
                <h3 className="text-xl sm:text-2xl font-bold text-invert-navy mb-3">
                    Thank you — we received your inquiry
                </h3>
                <p className="text-slate-600 leading-relaxed mb-6 max-w-md mx-auto">
                    A member of our team will review your details and reply within one business day with placement options and next steps.
                </p>
                <button
                    type="button"
                    className={ghostClass}
                    onClick={() => setSubmitted(false)}
                >
                    Submit another inquiry
                </button>
            </div>
        );
    }

    return (
        <div className={`${cardClass} ${className}`}>
            <form className="flex flex-col gap-5" onSubmit={handleSubmit} noValidate>
                <Field id="inquiry-company" label="Company Name" required error={errors.companyName}>
                    <input
                        id="inquiry-company"
                        type="text"
                        className={fieldClass}
                        placeholder="e.g. McDonald's"
                        value={form.companyName}
                        onChange={(e) => updateField('companyName', e.target.value)}
                        aria-invalid={Boolean(errors.companyName)}
                    />
                </Field>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field id="inquiry-fname" label="First Name" required error={errors.fname}>
                        <input
                            id="inquiry-fname"
                            type="text"
                            className={fieldClass}
                            placeholder="e.g. Jane"
                            value={form.fname}
                            onChange={(e) => updateField('fname', e.target.value)}
                            aria-invalid={Boolean(errors.fname)}
                        />
                    </Field>
                    <Field id="inquiry-lname" label="Last Name" required error={errors.lname}>
                        <input
                            id="inquiry-lname"
                            type="text"
                            className={fieldClass}
                            placeholder="e.g. Smith"
                            value={form.lname}
                            onChange={(e) => updateField('lname', e.target.value)}
                            aria-invalid={Boolean(errors.lname)}
                        />
                    </Field>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field id="inquiry-email" label="Email" required error={errors.email}>
                        <input
                            id="inquiry-email"
                            type="email"
                            className={fieldClass}
                            placeholder="you@company.com"
                            value={form.email}
                            onChange={(e) => updateField('email', e.target.value)}
                            aria-invalid={Boolean(errors.email)}
                        />
                    </Field>
                    <Field id="inquiry-phone" label="Phone">
                        <input
                            id="inquiry-phone"
                            type="tel"
                            className={fieldClass}
                            placeholder="(555) 555-5555"
                            value={form.phone}
                            onChange={(e) => updateField('phone', e.target.value)}
                        />
                    </Field>
                </div>

                <Field id="inquiry-goals" label="Campaign Goals">
                    <select
                        id="inquiry-goals"
                        className={selectClass}
                        style={selectBg}
                        value={form.campaignGoals}
                        onChange={(e) => updateField('campaignGoals', e.target.value)}
                    >
                        {GOAL_OPTIONS.map((opt) => (
                            <option key={opt.value || 'empty-goals'} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </Field>

                <Field id="inquiry-message" label="Message">
                    <textarea
                        id="inquiry-message"
                        rows={5}
                        className={`${fieldClass} resize-y min-h-[120px]`}
                        placeholder="Tell us about your campaign, target markets, and timeline."
                        value={form.message}
                        onChange={(e) => updateField('message', e.target.value)}
                    />
                </Field>

                {submitError && (
                    <p className="text-sm text-red-600" role="alert">{submitError}</p>
                )}

                <button
                    type="submit"
                    className={submitClass}
                    disabled={submitting}
                >
                    {submitting ? 'Submitting…' : 'Submit Inquiry'}
                </button>
            </form>
        </div>
    );
}
